'use client';

import React from 'react';
import Link from 'next/link';

const DEFAULT_AVATAR = '/images/demo-profile.png';

export default function TeacherReviewsSection({ profileData }) {
  const [reviewsModalOpen, setReviewsModalOpen] = React.useState(false);

  // Extract review data from profileData
  const reviewStats = profileData?.reviewStats || {};
  const reviews = reviewStats.reviews || [];
  const totalReviews = reviewStats.totalReviews || 0;
  const averageRating = reviewStats.averageRating || 0;

  const getInitials = (name) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map((word) => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getReviewerName = (review) =>
    review.userName || review.reviewerName || 'Anonymous';

  const getReviewProfileHref = (review) =>
    review.profileHref || (review.userId ? `/public-profile/${review.userId}` : null);

  const StarRating = ({ rating }) => {
    return (
      <div className="flex items-center gap-1">
        {[...Array(5)].map((_, i) => (
          <svg
            key={i}
            className={`h-4 w-4 ${i < rating ? 'fill-yellow-400 text-yellow-400' : 'fill-gray-200 text-gray-200'}`}
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
          </svg>
        ))}
      </div>
    );
  };

  if (totalReviews === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900">Student Reviews</h2>
            <div className="text-right">
              <div className="flex items-center gap-1 mb-1">
                <StarRating rating={0} />
                <span className="font-semibold text-gray-900">0</span>
              </div>
              <p className="text-sm text-gray-600">No reviews yet</p>
            </div>
          </div>
        </div>

        <div className="px-6 py-8">
          <div className="text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10m0 0V6a2 2 0 00-2-2H9a2 2 0 00-2 2v2m10 0v10a2 2 0 01-2 2H9a2 2 0 01-2-2V8m10 0H7" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No reviews yet</h3>
            <p className="text-gray-600">This teacher hasn&apos;t received any reviews from students yet.</p>
          </div>
        </div>
      </div>
    );
  }

  const displayReviews = reviews.slice(0, 3);

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">Student Reviews</h2>
          <div className="text-right">
            <div className="flex items-center gap-1 mb-1">
              <StarRating rating={Math.round(averageRating)} />
              <span className="font-semibold text-gray-900">{averageRating}</span>
            </div>
            <p className="text-sm text-gray-600">{totalReviews} {totalReviews === 1 ? 'review' : 'reviews'}</p>
          </div>
        </div>
      </div>

      <div className="px-6 py-4">
        <div className="h-96 overflow-y-auto">
          <div className="space-y-4">
            {displayReviews.map((review) => (
              <div key={review.id} className="border-b border-gray-100 pb-4 last:border-b-0">
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden">
                    {review.avatarUrl && review.avatarUrl !== DEFAULT_AVATAR ? (
                      <img
                        src={review.avatarUrl}
                        alt={getReviewerName(review)}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <span className="text-sm font-medium text-gray-600">
                        {getInitials(getReviewerName(review))}
                      </span>
                    )}
                  </div>

                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      {getReviewProfileHref(review) ? (
                        <Link
                          href={getReviewProfileHref(review)}
                          className="font-medium text-gray-900 hover:underline"
                        >
                          {getReviewerName(review)}
                        </Link>
                      ) : (
                        <h4 className="font-medium text-gray-900">{getReviewerName(review)}</h4>
                      )}
                      <span className="text-xs text-gray-500">{formatDate(review.postedAt)}</span>
                    </div>
                    <div className="flex items-center gap-2 mb-2">
                      <StarRating rating={review.rating} />
                      {review.courseName && (
                        <span className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
                          {review.courseName}
                        </span>
                      )}
                    </div>
                    <p className="text-gray-700 text-sm mt-2 leading-relaxed">{review.content}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {totalReviews > 3 && (
          <div className="border-t border-gray-200 mt-4 pt-4">
            <button
              onClick={() => setReviewsModalOpen(true)}
              className="w-full bg-transparent border border-gray-300 rounded-md px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-colors"
            >
              Show All {totalReviews} Reviews
            </button>
          </div>
        )}
      </div>

      {reviewsModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold">All Reviews for Teacher</h2>
                <button
                  onClick={() => setReviewsModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="h-96 overflow-y-auto p-6">
              <div className="space-y-4">
                {reviews.map((review) => (
                  <div key={review.id} className="border-b border-gray-100 pb-4 last:border-b-0">
                    <div className="flex items-start gap-3">
                      <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden">
                        {review.avatarUrl && review.avatarUrl !== DEFAULT_AVATAR ? (
                          <img
                            src={review.avatarUrl}
                            alt={getReviewerName(review)}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <span className="text-sm font-medium text-gray-600">
                            {getInitials(getReviewerName(review))}
                          </span>
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          {getReviewProfileHref(review) ? (
                            <Link
                              href={getReviewProfileHref(review)}
                              className="font-medium text-gray-900 hover:underline"
                            >
                              {getReviewerName(review)}
                            </Link>
                          ) : (
                            <h4 className="font-medium text-gray-900">{getReviewerName(review)}</h4>
                          )}
                          <span className="text-xs text-gray-500">{formatDate(review.postedAt)}</span>
                        </div>
                        <div className="flex items-center gap-2 mb-2">
                          <StarRating rating={review.rating} />
                          {review.courseName && (
                            <span className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
                              {review.courseName}
                            </span>
                          )}
                        </div>
                        <p className="text-gray-700 text-sm mt-2 leading-relaxed">{review.content}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
